import type { DocumentNode } from 'graphql'

import type { ContentfulClient } from '@graphql/client'
import { getOrCreateContentfulClient } from '@graphql/client'
import { firstFromCollection } from '@graphql/collection'
import type { PageMetaFragment } from '@graphql/fragments/PageMetaFragment.graphql'
import { requestQuery } from '@graphql/functions'
import { getEmbedded } from '@graphql/hooks/embedded'
import { getVideo } from '@graphql/hooks/video'
import { GetAnnouncementDocument } from '@graphql/queries/GetAnnouncement.graphql'
import { GetBannerDocument } from '@graphql/queries/GetBanner.graphql'
import { GetComparisonTableSectionDocument } from '@graphql/queries/GetComparisonTableSection.graphql'
import { GetCreditMattersTableDocument } from '@graphql/queries/GetCreditMattersTable.graphql'
import { GetCtaSectionDocument } from '@graphql/queries/GetCTASection.graphql'
import { GetEmbeddedDocument } from '@graphql/queries/GetEmbedded.graphql'
import { GetEmbeddedTypeFormDocument } from '@graphql/queries/GetEmbeddedTypeform.graphql'
import { GetFeatureSectionThreeColumnDocument } from '@graphql/queries/GetFeatureSectionThreeColumn.graphql'
import { GetFeatureSectionV2Document } from '@graphql/queries/GetFeatureSectionV2.graphql'
import { GetFormSectionDocument } from '@graphql/queries/GetFormSection.graphql'
import { GetFullWidthVideoDocument } from '@graphql/queries/GetFullWidthVideo.graphql'
import { GetHeroDocument } from '@graphql/queries/GetHero.graphql'
import { GetHeroV2Document } from '@graphql/queries/GetHeroV2.graphql'
import { GetHorizontalScrollerDocument } from '@graphql/queries/GetHorizontalScroller.graphql'
import { GetMarqueeDocument } from '@graphql/queries/GetMarquee.graphql'
import { GetMultiCopySectionDocument } from '@graphql/queries/GetMultiCopySection.graphql'
import { GetMultiImageSectionDocument } from '@graphql/queries/GetMultiImageSection.graphql'
import { GetNineBlockGridDocument } from '@graphql/queries/GetNineBlockGrid.graphql'
import type { PageFragment } from '@graphql/queries/GetPage.graphql'
import { GetPageDocument } from '@graphql/queries/GetPage.graphql'
import { GetPageMetaDocument } from '@graphql/queries/GetPageMeta.graphql'
import { GetPhoneScrollerDocument } from '@graphql/queries/GetPhoneScroller.graphql'
import { GetSectionDocument } from '@graphql/queries/GetSection.graphql'
import { GetSectionOfBentoBoxesDocument } from '@graphql/queries/GetSectionOfBentoBoxes.graphql'
import { GetSectionOfFaQsDocument } from '@graphql/queries/GetSectionOfFAQs.graphql'
import { GetSectionOfHeroesDocument } from '@graphql/queries/GetSectionOfHeroes.graphql'
import { GetSectionOfInvestorsDocument } from '@graphql/queries/GetSectionOfInvestors.graphql'
import { GetSectionOfPressBannersDocument } from '@graphql/queries/GetSectionOfPressBanners.graphql'
import { GetSectionOfReviewsDocument } from '@graphql/queries/GetSectionOfReviews.graphql'
import { GetSectionOfThumbnailsDocument } from '@graphql/queries/GetSectionOfThumbnails.graphql'
import { GetSideBySideDocument } from '@graphql/queries/GetSideBySide.graphql'
import { GetSignupIntentsSectionDocument } from '@graphql/queries/GetSignupIntentsSection.graphql'
import { GetSimpleHeaderSectionDocument } from '@graphql/queries/GetSimpleHeaderSection.graphql'
import { GetSectionOfStepsDocument } from '@graphql/queries/GetSteps.graphql'
import { GetVideoDocument } from '@graphql/queries/GetVideo.graphql'
import { logger } from '@lib/logger'
import { ERRORS } from '@lib/utils/errors'
import { ThemeOptions } from '@lib/utils/withTheme'

type PageSectionFragmentType = NonNullable<
    PageFragment['sections']
>['items'][0]['type']

type PageSectionItemType = NonNullable<PageFragment['sections']>['items'][0]

export const fetchContentfulPage = async (slug: string) => {
    const version = process.env.NEXT_PUBLIC_PAGE_VERSION_SUFFIX
    if (version && !slug.endsWith(version)) {
        try {
            return await fetchContentfulPageBySlug(slug + version)
        } catch (error) {
            console.warn(
                `Could not find page for version suffix ${version}, falling back to default`,
            )
        }
    }
    const page = await fetchContentfulPageBySlug(slug)
    return page
}

const fetchContentfulPageBySlug = async (slug: string) => {
    const client = getOrCreateContentfulClient('/')

    const data = await requestQuery(client, {
        query: GetPageDocument,
        variables: { slug, includeBanner: true, includeBody: true },
    })

    if (!data?.data?.pages?.items?.length) {
        throw new Error(`${ERRORS.PAGE_NOT_FOUND}: ${slug}`)
    }

    const page = firstFromCollection(data.data.pages)

    if (!page) {
        throw new Error(`${ERRORS.PAGE_NOT_FOUND}: ${slug}`)
    }

    const sectionIds = page?.sections?.items as Array<{
        type: PageSectionFragmentType
        sys: { id: string }
    }>

    // fetch meta and sections for the page
    const [metaData, sections] = await Promise.all([
        fetchContentfulPageMetaData(client, page?.meta?.sys?.id as string),
        fetchContentfulPageSections(client, sectionIds),
    ])

    return {
        ...page,
        theme: {
            key: ThemeOptions.black, // set a default theme for pages that dont have one published in contentful yet
            ...(page?.theme || {}),
        },
        meta: metaData,
        sections: {
            items: sections ?? [],
        },
    }
}

export const fetchContentfulPageSections = async (
    client: ContentfulClient,
    sections: { type: PageSectionFragmentType; sys: { id: string } }[],
): Promise<Array<PageSectionItemType>> => {
    // map section type => graphql query
    const typeToQueryMap: Partial<
        Record<PageSectionFragmentType, DocumentNode>
    > = {
        // legacy sections
        Announcement: GetAnnouncementDocument,
        Embedded: GetEmbeddedDocument,
        Section: GetSectionDocument,
        SectionOfInvestors: GetSectionOfInvestorsDocument,
        SectionOfPressBanners: GetSectionOfPressBannersDocument,
        Steps: GetSectionOfStepsDocument,
        Video: GetVideoDocument,
        // newly added sections
        CtaSection: GetCtaSectionDocument,
        EmbeddedTypeform: GetEmbeddedTypeFormDocument,
        FullWidthVideo: GetFullWidthVideoDocument,
        FeatureSectionThreeColumn: GetFeatureSectionThreeColumnDocument,
        FeatureSectionV2: GetFeatureSectionV2Document,
        Hero: GetHeroDocument,
        HeroV2: GetHeroV2Document,
        HeroV2Carousel: GetSectionOfHeroesDocument,
        BentoBox: GetSectionOfBentoBoxesDocument,
        SimpleHeaderSection: GetSimpleHeaderSectionDocument,
        SignupIntents: GetSignupIntentsSectionDocument,
        CreditMattersTable: GetCreditMattersTableDocument,
        NineBlockGrid: GetNineBlockGridDocument,
        HorizontalScroller: GetHorizontalScrollerDocument,
        Marquee: GetMarqueeDocument,
        MultiCopySection: GetMultiCopySectionDocument,
        MultiImageSection: GetMultiImageSectionDocument,
        PhoneScroller: GetPhoneScrollerDocument,
        SideBySide: GetSideBySideDocument,
        SectionOfReviews: GetSectionOfReviewsDocument,
        SectionOfFaQs: GetSectionOfFaQsDocument,
        SectionOfThumbnails: GetSectionOfThumbnailsDocument,
        Banner: GetBannerDocument,
        FormSection: GetFormSectionDocument,
        ComparisonTable: GetComparisonTableSectionDocument,
    }

    // map all sections to a graphql query by section type
    const requests = sections
        ?.map((section) => {
            const key = section.type

            if (!typeToQueryMap[key]) return

            const query = typeToQueryMap[key]

            if (!query) return

            return requestQuery(client, {
                query,
                variables: { id: section.sys.id },
            }).then(async (item: any) => {
                // if type is section of content and has a video, fetch the nested video too
                if (key === 'Section' && !!item.data.section.video) {
                    try {
                        const video = item.data.section.video
                        const method =
                            video.type === 'Embedded' ? getEmbedded : getVideo
                        const responseKey =
                            video.type === 'Embedded' ? 'embedded' : 'video'
                        const result = await method(client, {
                            variables: { id: video.sys.id },
                        })

                        return {
                            ...item?.data?.section,
                            video: {
                                ...item.data.section.video,
                                ...(result.data as any)[responseKey],
                            },
                        }
                    } catch (error) {
                        logger.error('Failed to fetch nested video', {
                            error,
                        })
                    }
                }

                return item?.data?.section || item?.data?.embedded
            })
        })
        .filter(Boolean)

    if (!requests) {
        return []
    }

    // fetch all section queries
    const sectionItems: PageSectionItemType[] = await Promise.all(requests)

    return sectionItems
}

export const fetchContentfulPageMetaData = async (
    client: ContentfulClient,
    id: string,
): Promise<PageMetaFragment | undefined> => {
    if (!id) {
        return
    }

    const response = await requestQuery(client, {
        query: GetPageMetaDocument,
        variables: { id },
    }).then((res) => res?.data?.meta)

    return response
}
